import { useState, useEffect, useMemo } from "react";
import Navigation from "@/components/Navigation";
import MasonryGallery from "@/components/MasonryGallery";
import Footer from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";
import { useSEO } from "@/hooks/useCanonical";
import { getStaticRouteSeo } from "@/lib/seo";
import { buildHomeJsonLd } from "@/lib/structuredData";

const extractFilenameFromUrl = (url: string): string => {
  try {
    const urlPath = new URL(url).pathname;
    const filename = urlPath.split("/").pop() || "";
    // Remove file extension and decode URI components
    return decodeURIComponent(filename.replace(/\.[^/.]+$/, "").replace(/[-_]/g, " "));
  } catch {
    return "";
  }
};

const Index = () => {
  const [homeImages, setHomeImages] = useState<
    Array<{
      url: string;
      caption: string | null;
      job_slug: string | null;
      category_slug: string | null;
    }>
  >([]);
  const [isLoading, setIsLoading] = useState(true);

  // Create JSON-LD dynamically
  const jsonLd = useMemo(() => buildHomeJsonLd(homeImages), [homeImages]);

  const seo = getStaticRouteSeo("home", { image: homeImages[0]?.url });

  useSEO({ ...seo, jsonLd });

  useEffect(() => {
    const fetchHomeImages = async () => {
      const { data } = await supabase
        .from("images")
        .select(
          `
          url, 
          caption,
          job_id,
          jobs!inner(slug, category_id, categories!inner(slug))
        `,
        )
        .eq("show_on_home", true)
        .order("home_order");

      if (data && data.length > 0) {
        setHomeImages(
          data.map((img) => ({
            url: img.url,
            caption: img.caption,
            job_slug: img.jobs?.slug || null,
            category_slug: img.jobs?.categories?.slug || null,
          })),
        );
      }
      setIsLoading(false);
    };

    fetchHomeImages();
  }, []);

  const galleryImages = homeImages.map((img, idx) => {
    const filename = extractFilenameFromUrl(img.url);
    return {
      src: img.url,
      alt: img.caption || filename || "Portfolio image",
      title: filename,
      height: 500 + (idx % 3) * 100,
      link: img.job_slug && img.category_slug ? `/work/${img.category_slug}/${img.job_slug}` : undefined,
    };
  });

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navigation />
      <main className="pt-32 pb-16 flex-1">
        {/* Visually hidden h1 for SEO - screen readers and search engines can see it */}
        <h1 className="sr-only">Ina Nederdal – Porträtt- och Lifestyle-fotograf i Stockholm</h1>
        {isLoading ? (
          <div className="px-4 sm:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
              {[...Array(9)].map((_, i) => (
                <div
                  key={i}
                  className="animate-pulse bg-muted rounded-sm"
                  style={{ height: `${400 + (i % 3) * 100}px` }}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto">
            <MasonryGallery images={galleryImages} gapSize="large" />
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Index;
